'use client';

import type {
  AnalyticsService,
  UserInformationService,
} from '@inato/browser-tracking';
import {
  AlertingServiceProvider,
  AnalyticsServiceProvider,
  CompositeAlertingService,
  CompositeAnalyticsService,
  CompositeUserInformationService,
  LogAlertingService,
  SentryAlertingService,
  UserInformationServiceProvider,
} from '@inato/browser-tracking';
import { NotificationContainer, PanelRootElementProvider } from '@inato/ui';
import { browserTracingIntegration } from '@sentry/browser';
import { Predicate } from 'effect';
import { HelmetProvider } from 'react-helmet-async';

import { Notification } from '@components/Notification';
import { useFakeAnalyticsService } from './buildFakeAnalyticsService';
import { buildFeatureFlagsService } from './buildFeatureFlagsService';
import { buildSegmentAnalyticsService } from './buildSegmentAnalyticsService';
import { DevCdaAutosignService } from './infrastructure/CdaAutosignService/DevCdaAutosignService';
import { NoOpCdaAutosignService } from './infrastructure/CdaAutosignService/NoOpCdaAutosignService';
import { CookieCookieConsentService } from './infrastructure/CookieCookieConsentService';
import { HttpFileUploadService } from './infrastructure/HttpFileUploadService';
import { InatoHealthCheckServiceImpl } from './infrastructure/InatoHealthCheckServiceImpl';
import { NextRuntimeEnvironmentService } from './infrastructure/NextRuntimeEnvironmentService';
import { PersistentStorageServiceImpl } from './infrastructure/PersistentStorageServiceImpl';
import { SimpleNotificationService } from './infrastructure/SimpleNotificationService';
import { buildClinicalTrialsTrialInformationFetchService } from './infrastructure/TrialInformationFetchService/ClinicalTrialsTrialInformationFetchService';
import { buildFakeTrialInformationFetchService } from './infrastructure/TrialInformationFetchService/FakeTrialInformationFetchService';
import { WebBrowserPlatformService } from './infrastructure/WebBrowserPlatformService';
import { CdaAutosignServiceProvider } from './presentation/CdaAutosignContext';
import { EnvironmentServiceProvider } from './presentation/EnvironmentContext';
import { ErrorBoundary } from './presentation/ErrorBoundary';
import { InatoHealthCheckServiceProvider } from './presentation/InatoHealthCheckContext';
import { PersistentStorageServiceProvider } from './presentation/PersistentStorageContext';
import { PlatformServiceProvider } from './presentation/PlatformContext';
import { TrialInformationFetchServiceProvider } from './presentation/TrialInformationFetchServiceContext';
import { DisplayNotificationSubscriber } from './presentation/notificationService/DisplayNotificationSubscriber';
import { NotificationServiceProvider } from './presentation/notificationService/NotificationServiceContext';
import { CookieConsentServiceProvider } from './presentation/shared/contexts/CookieConsentContext';
import { FeatureFlagsServiceProvider } from './presentation/shared/contexts/FeatureFlagsServiceContext';
import { FileUploadServiceProvider } from './presentation/shared/contexts/FileUploadContext';
import { useBuildLoggingService } from './useBuildLoggingService';

const Providers = ({
  children,
  env,
}: {
  children: React.ReactNode;
  env: Record<string, string | undefined>;
}) => {
  const environmentService = new NextRuntimeEnvironmentService(env);
  const baseApiUrl = environmentService.get('PUBLIC_BASE_API_URL');

  const loggingService = useBuildLoggingService({ environmentService });

  const logAlertingService = new LogAlertingService(loggingService);

  const sentryAlertingService = new SentryAlertingService({
    sentryConfig: {
      dsn: environmentService.getPossiblyUndefined('PUBLIC_SENTRY_DSN'),
      environment: environmentService.getPossiblyUndefined('PUBLIC_SENTRY_ENV'),
      ...(environmentService.getBoolean(
        'PUBLIC_SENTRY_PREVENT_RELEASE_INJECTION',
      )
        ? { release: undefined } // Do not inject release info into the bundle in development to avoid e2e tests failures linked to fake clock
        : {}),
      tracesSampleRate: 0.1,
      ignoreErrors: [
        // errors caused by a crawler bot
        'Object Not Found Matching Id:',
        // errors linked to module not being present in the cdn
        'Failed to fetch dynamically imported module:',
        // Next.js internal error when invoking redirect()
        // more details in: https://www.notion.so/inato/How-to-do-a-redirection-navigation-in-NextJS
        'NEXT_REDIRECT',
        // Next.js internal error when invoking notFound()
        // more details here: https://nextjs.org/docs/app/api-reference/functions/not-found
        'NEXT_HTTP_ERROR_FALLBACK;404',
      ],
      tracePropagationTargets: [/^\//, new URL(baseApiUrl).hostname],
    },
    sentryIntegrations: [browserTracingIntegration()],
  });

  const featureFlagsService = buildFeatureFlagsService(environmentService);

  const compositeAlertingService = new CompositeAlertingService([
    logAlertingService,
    sentryAlertingService,
  ]);

  const segmentService = buildSegmentAnalyticsService({
    environmentService,
  });

  const fakeAnalyticsService = useFakeAnalyticsService({
    environmentService,
    loggingService,
  });

  const analyticsServices: Array<AnalyticsService> = [
    segmentService,
    fakeAnalyticsService,
  ].filter(Predicate.isNotNull);
  const compositeAnalyticsService = new CompositeAnalyticsService(
    analyticsServices,
  );

  const userInformationServices: Array<UserInformationService> = [
    sentryAlertingService,
    segmentService,
    fakeAnalyticsService,
  ].filter(Predicate.isNotNull);
  const compositeUserInformationService = new CompositeUserInformationService(
    userInformationServices,
  );

  const notificationService = new SimpleNotificationService();

  const cookieConsentService = new CookieCookieConsentService();

  const platformService = new WebBrowserPlatformService();

  const cdaAutosignEnabled = environmentService.getPossiblyUndefined(
    'PUBLIC_CDA_AUTOSIGN',
  );
  const cdaAutosignService =
    cdaAutosignEnabled === 'true'
      ? new DevCdaAutosignService()
      : new NoOpCdaAutosignService();

  const fileUploadService = new HttpFileUploadService({
    baseApiUrl,
  });

  const InatoHealthCheckService = new InatoHealthCheckServiceImpl(baseApiUrl);

  const persistentStorageService = new PersistentStorageServiceImpl();

  const trialInformationFetchService = environmentService.getBoolean(
    'PUBLIC_USE_FAKE_TRIAL_INFORMATION_FETCH_SERVICE',
  )
    ? buildFakeTrialInformationFetchService()
    : buildClinicalTrialsTrialInformationFetchService({
        alertingService: compositeAlertingService,
      });

  return (
    <HelmetProvider>
      <NotificationServiceProvider value={notificationService}>
        <PanelRootElementProvider>
          <PlatformServiceProvider value={platformService}>
            <EnvironmentServiceProvider value={environmentService}>
              <InatoHealthCheckServiceProvider value={InatoHealthCheckService}>
                <ErrorBoundary
                  alertingService={compositeAlertingService}
                  platformService={platformService}
                  persistentStorageService={persistentStorageService}
                >
                  <FeatureFlagsServiceProvider value={featureFlagsService}>
                    <CookieConsentServiceProvider value={cookieConsentService}>
                      <AlertingServiceProvider value={compositeAlertingService}>
                        <AnalyticsServiceProvider
                          value={compositeAnalyticsService}
                        >
                          <UserInformationServiceProvider
                            value={compositeUserInformationService}
                          >
                            <PersistentStorageServiceProvider
                              value={persistentStorageService}
                            >
                              <FileUploadServiceProvider
                                value={fileUploadService}
                              >
                                <CdaAutosignServiceProvider
                                  value={cdaAutosignService}
                                >
                                  <DisplayNotificationSubscriber
                                    NotificationContainerComponent={
                                      NotificationContainer
                                    }
                                    DefaultNotificationComponent={Notification}
                                  />
                                  <TrialInformationFetchServiceProvider
                                    value={trialInformationFetchService}
                                  >
                                    {children}
                                  </TrialInformationFetchServiceProvider>
                                </CdaAutosignServiceProvider>
                              </FileUploadServiceProvider>
                            </PersistentStorageServiceProvider>
                          </UserInformationServiceProvider>
                        </AnalyticsServiceProvider>
                      </AlertingServiceProvider>
                    </CookieConsentServiceProvider>
                  </FeatureFlagsServiceProvider>
                </ErrorBoundary>
              </InatoHealthCheckServiceProvider>
            </EnvironmentServiceProvider>
          </PlatformServiceProvider>
        </PanelRootElementProvider>
      </NotificationServiceProvider>
    </HelmetProvider>
  );
};

export default Providers;
